import toCurrency from '@orn/shared-web/utils/money/toCurrency';

import { FinancialAccountPrimaryType } from '../../__generated__/globalTypes';

export const EMPTY_CELL = '—';

const PRICE_MAX_FRACTION_DIGITS = 6;
/** Below one major unit a balance-to-holdings gap is rounding, not a discrepancy. */
const NOTABLE_GAP_MAJOR_UNITS = 1;

export type FlagTone = 'warning' | 'muted';

export interface IFlag {
  label: string;
  tone: FlagTone;
}

export interface IMoneyFormatOptions {
  isoCurrencyCode?: string | null;
  /** Set instead of the ISO code for a currency with none, such as a cryptocurrency. */
  unofficialCurrencyCode?: string | null;
  /** A unit price can sit well below a cent; an amount cannot. */
  isUnitPrice?: boolean;
}

/** A major-unit figure for a cell. An unofficial currency renders as the bare number with its code beside it. */
export function formatMajorUnits(value: number | null | undefined, options: IMoneyFormatOptions = {}): string {
  if (value == null) {
    return EMPTY_CELL;
  }
  const maxFractionDigits = options.isUnitPrice ? PRICE_MAX_FRACTION_DIGITS : undefined;
  if (!options.isoCurrencyCode && options.unofficialCurrencyCode) {
    return `${toCurrency(value, { symbol: false, maxFractionDigits })} ${options.unofficialCurrencyCode}`;
  }
  return toCurrency(value, { currencyCode: options.isoCurrencyCode, maxFractionDigits });
}

/** A transaction's money figure. A row that names no currency shows the bare number, not a guessed symbol. */
export function formatTransactionMoney(value: number | null | undefined, options: IMoneyFormatOptions = {}): string {
  if (value != null && !options.isoCurrencyCode && !options.unofficialCurrencyCode) {
    return toCurrency(value, {
      symbol: false,
      maxFractionDigits: options.isUnitPrice ? PRICE_MAX_FRACTION_DIGITS : undefined,
    });
  }
  return formatMajorUnits(value, options);
}

export interface IAvailableOrLimitInput {
  type: FinancialAccountPrimaryType;
  availableBalance: number | null | undefined;
  creditLimit: number | null | undefined;
  currencyCode: string | null | undefined;
}

/** The second money column: a credit account's limit, any other account's available balance. */
export function availableOrLimitLabel(input: IAvailableOrLimitInput): string {
  const { type, availableBalance, creditLimit, currencyCode } = input;
  if (type === FinancialAccountPrimaryType.Credit) {
    return creditLimit == null
      ? EMPTY_CELL
      : `${formatMajorUnits(creditLimit, { isoCurrencyCode: currencyCode })} limit`;
  }
  return formatMajorUnits(availableBalance, { isoCurrencyCode: currencyCode });
}

export interface IAuditCounts {
  relinkCandidate: boolean;
  positionsMissingPrice: number;
  positionsMissingCostBasis: number;
  positionsWithUnidentifiedSecurity: number;
  positionsQuantityChangedWithoutPriceChange: number;
}

/** The collapsed row's badges. A missing cost basis is routine (crypto rarely reports one), so it reads muted. */
export function accountRowFlags(audit: IAuditCounts | null | undefined): IFlag[] {
  if (!audit) {
    return [];
  }
  const flags: IFlag[] = [];
  if (audit.relinkCandidate) {
    flags.push({ label: 'Relink', tone: 'warning' });
  }
  if (audit.positionsMissingPrice > 0) {
    flags.push({ label: `${audit.positionsMissingPrice} no price`, tone: 'warning' });
  }
  if (audit.positionsWithUnidentifiedSecurity > 0) {
    flags.push({ label: `${audit.positionsWithUnidentifiedSecurity} unidentified`, tone: 'warning' });
  }
  if (audit.positionsQuantityChangedWithoutPriceChange > 0) {
    flags.push({ label: `${audit.positionsQuantityChangedWithoutPriceChange} stale qty`, tone: 'warning' });
  }
  if (audit.positionsMissingCostBasis > 0) {
    flags.push({ label: `${audit.positionsMissingCostBasis} no cost`, tone: 'muted' });
  }
  return flags;
}

export interface IPositionFlagsInput {
  missingPrice: boolean;
  missingCostBasis: boolean;
  unidentifiedSecurity: boolean;
  quantityChangedWithoutPriceChange: boolean;
  isCashEquivalent?: boolean | null;
}

export function positionFlags(position: IPositionFlagsInput): IFlag[] {
  const flags: IFlag[] = [];
  if (position.missingPrice) {
    flags.push({ label: 'No price', tone: 'warning' });
  }
  if (position.unidentifiedSecurity) {
    flags.push({ label: 'Unidentified', tone: 'warning' });
  }
  if (position.quantityChangedWithoutPriceChange) {
    flags.push({ label: 'Stale qty', tone: 'warning' });
  }
  if (position.missingCostBasis) {
    flags.push({ label: 'No cost', tone: 'muted' });
  }
  if (position.isCashEquivalent) {
    flags.push({ label: 'Cash', tone: 'muted' });
  }
  return flags;
}

/** Largest position first; one the institution gave no value for sorts last. */
export function sortPositionsByValue<T extends { institutionValue?: number | null }>(positions: readonly T[]): T[] {
  return [...positions].sort(
    (a, b) => (b.institutionValue ?? Number.NEGATIVE_INFINITY) - (a.institutionValue ?? Number.NEGATIVE_INFINITY)
  );
}

export type HoldingsState = 'relink' | 'never' | 'empty' | 'populated';

export interface IHoldingsStateInput {
  relinkCandidate: boolean;
  latestSnapshotAt?: string | null;
  positionCount: number;
}

/**
 * What the holdings section has to say before any table. A relink candidate
 * with no snapshot leads with the relink, since no sync can fix it; one that
 * still has an older snapshot shows it.
 */
export function holdingsState(audit: IHoldingsStateInput): HoldingsState {
  if (!audit.latestSnapshotAt) {
    return audit.relinkCandidate ? 'relink' : 'never';
  }
  return audit.positionCount === 0 ? 'empty' : 'populated';
}

/** Whether the gap cell earns a warning tint. Positions priced in an unofficial currency make the sum incomparable. */
export function isGapNotable(
  balanceGap: number | null | undefined,
  positions: readonly { isoCurrencyCode?: string | null; unofficialCurrencyCode?: string | null }[] = []
): boolean {
  if (balanceGap == null) {
    return false;
  }
  if (positions.some((position) => !position.isoCurrencyCode && position.unofficialCurrencyCode)) {
    return false;
  }
  return Math.abs(balanceGap) >= NOTABLE_GAP_MAJOR_UNITS;
}

export interface ITransactionCoverageInput {
  count: number;
  earliestDate?: string | null;
  latestDate?: string | null;
}

/**
 * The line above the investment transactions table: how many rows are stored,
 * the posting dates they span, and how many of them the table lists when it
 * does not list them all.
 */
export function describeTransactionCoverage(coverage: ITransactionCoverageInput, shownCount: number): string {
  if (coverage.count === 0) {
    return 'No investment transactions stored.';
  }
  const parts = [`${coverage.count.toLocaleString('en-US')} stored`];
  if (coverage.earliestDate && coverage.latestDate) {
    parts.push(
      coverage.earliestDate === coverage.latestDate
        ? coverage.latestDate
        : `${coverage.earliestDate} → ${coverage.latestDate}`
    );
  }
  if (shownCount < coverage.count) {
    parts.push(`newest ${shownCount.toLocaleString('en-US')} shown`);
  }
  return parts.join(' · ');
}

/** The type column: the provider's type and subtype as stored, once when they are the same word. */
export function formatTransactionType(transaction: { type: string; subtype: string }): string {
  return transaction.type === transaction.subtype ? transaction.type : `${transaction.type} / ${transaction.subtype}`;
}

/** The alert-settings line: an account's own thresholds, or that it inherits the family's. */
export function describeAlertSettings(settings: {
  lowBalanceThreshold?: number | null;
  creditUtilizationThreshold?: number | null;
  disableThreshold?: boolean | null;
}): string {
  if (settings.disableThreshold) {
    return 'Alerts disabled for this account';
  }
  const parts: string[] = [];
  if (settings.lowBalanceThreshold != null) {
    parts.push(`low balance below ${formatMajorUnits(settings.lowBalanceThreshold)}`);
  }
  if (settings.creditUtilizationThreshold != null) {
    parts.push(`utilization at ${settings.creditUtilizationThreshold}%`);
  }
  return parts.length === 0 ? 'Inherits family thresholds' : parts.join(' · ');
}
